import StoreDirectory from "../StoreDirectory";
import { getEXStores, addEXStore, updateEXStore, deleteEXStore, syncEXStoresFromPOs } from "../../lib/api";
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Store, Plus, Trash2, RefreshCw, Search, Edit2, X, Check, MapPin, Mail, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function EXStoreDirectory() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [newStore, setNewStore] = useState({ store_name: "", address: "", email: "", phone: "", gst: "", city: "", pincode: "", contact_person: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getEXStores(); setStores(r.data.stores || []); } catch { toast.error("Failed to load EXTREME stores"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = stores.filter(s => s.store_name?.toLowerCase().includes(search.toLowerCase()) || s.city?.toLowerCase().includes(search.toLowerCase()));

  const handleSync = async () => { setSyncing(true); try { const r = await syncEXStoresFromPOs(); toast.success(r.data.message); await load(); } catch { toast.error("Sync failed"); } setSyncing(false); };

  const handleAdd = async () => {
    if (!newStore.store_name.trim()) return;
    try { await addEXStore(newStore); toast.success("Added"); setNewStore({ store_name: "", address: "", email: "", phone: "", gst: "", city: "", pincode: "", contact_person: "" }); setShowAdd(false); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleUpdate = async () => {
    if (!editingStore) return;
    try { await updateEXStore(editingStore.store_name, editingStore); toast.success("Updated"); setEditingStore(null); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Remove "${name}"?`)) return;
    try { await deleteEXStore(name); toast.success("Removed"); setStores(prev => prev.filter(s => s.store_name !== name)); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="ex-store-directory">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div><h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Store className="w-5 h-5" /> EXTREME Store Directory</h1><p className="text-sm text-slate-500">{stores.length} stores</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="ex-sync-stores"><RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />Sync from POs</Button>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)}><Plus className="w-4 h-4 mr-1" />Add Store</Button>
        </div>
      </div>

      {showAdd && (
        <Card><CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input placeholder="Store Name *" value={newStore.store_name} onChange={e => setNewStore({...newStore, store_name: e.target.value})} />
            <Input placeholder="Contact Person" value={newStore.contact_person} onChange={e => setNewStore({...newStore, contact_person: e.target.value})} />
            <Input placeholder="Email" value={newStore.email} onChange={e => setNewStore({...newStore, email: e.target.value})} />
            <Input placeholder="Phone" value={newStore.phone} onChange={e => setNewStore({...newStore, phone: e.target.value})} />
            <Input placeholder="Address" value={newStore.address} onChange={e => setNewStore({...newStore, address: e.target.value})} className="col-span-2" />
            <Input placeholder="City" value={newStore.city} onChange={e => setNewStore({...newStore, city: e.target.value})} />
            <Input placeholder="GST" value={newStore.gst} onChange={e => setNewStore({...newStore, gst: e.target.value})} />
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={handleAdd}><Check className="w-4 h-4 mr-1" />Add</Button><Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button></div>
        </CardContent></Card>
      )}

      <div className="relative max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>

      <Card className="overflow-hidden">
        <ScrollArea className="max-h-[500px]">
          <Table data-testid="ex-stores-table">
            <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs">Store</TableHead><TableHead className="text-xs">Contact</TableHead><TableHead className="text-xs">Email / Phone</TableHead><TableHead className="text-xs">City</TableHead><TableHead className="text-xs">GST</TableHead><TableHead className="text-xs w-20">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map(s => {
                const isEditing = editingStore?.store_name === s.store_name;
                return (
                  <TableRow key={s.store_name} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-sm"><Building2 className="w-3.5 h-3.5 text-slate-400 inline mr-1" />{s.store_name}</TableCell>
                    <TableCell className="text-sm">{isEditing ? <Input value={editingStore.contact_person} onChange={e => setEditingStore({...editingStore, contact_person: e.target.value})} className="h-7 text-xs" /> : s.contact_person || "-"}</TableCell>
                    <TableCell className="text-xs">{isEditing ? <><Input value={editingStore.email} onChange={e => setEditingStore({...editingStore, email: e.target.value})} className="h-7 text-xs mb-1" /><Input value={editingStore.phone} onChange={e => setEditingStore({...editingStore, phone: e.target.value})} className="h-7 text-xs" /></> : <>{s.email && <p>{s.email}</p>}{s.phone && <p className="text-slate-500">{s.phone}</p>}{!s.email && !s.phone && "-"}</>}</TableCell>
                    <TableCell className="text-xs">{isEditing ? <Input value={editingStore.city} onChange={e => setEditingStore({...editingStore, city: e.target.value})} className="h-7 text-xs" /> : s.city || "-"}</TableCell>
                    <TableCell className="text-xs font-mono">{isEditing ? <Input value={editingStore.gst} onChange={e => setEditingStore({...editingStore, gst: e.target.value})} className="h-7 text-xs" /> : s.gst || "-"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {isEditing ? <><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" onClick={handleUpdate}><Check className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingStore(null)}><X className="w-3.5 h-3.5" /></Button></> :
                        <><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => setEditingStore({...s})}><Edit2 className="w-3.5 h-3.5" /></Button><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => handleDelete(s.store_name)}><Trash2 className="w-3.5 h-3.5" /></Button></>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
    </div>
  );
}
